import { QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';

import { AuthStaleError, KycRequiredError, SessionTerminatedError } from './api/client.ts';
import { rootRoute } from './routes/__root.tsx';
import { accountRoute } from './routes/account.tsx';
import { changePinRoute } from './routes/change-pin.tsx';
import { cardsRoute } from './routes/index.tsx';
import { kycPendingRoute } from './routes/kyc-pending.tsx';
import { loggedOutRoute } from './routes/logged-out.tsx';
import { newCardRoute } from './routes/new-card.tsx';
import { securityRoute } from './routes/security.tsx';
import { shipRoute } from './routes/ship.tsx';
import { supportRoute } from './routes/support.tsx';
import { topupFromVaultRoute } from './routes/topup-from-vault.tsx';
import { topupRoute } from './routes/topup.tsx';
import { transactionsRoute } from './routes/transactions.tsx';
import { vaultRoute } from './routes/vault.tsx';
import { walletHistoryRoute } from './routes/wallet-history.tsx';
import { walletSendRoute } from './routes/wallet-send.tsx';

// The one route tree every surface and the route-spec harness mount, so a
// route added or removed here is exercised by the whole suite.
export const routeTree = rootRoute.addChildren([
  cardsRoute,
  topupRoute,
  topupFromVaultRoute,
  transactionsRoute,
  accountRoute,
  loggedOutRoute,
  kycPendingRoute,
  newCardRoute,
  changePinRoute,
  shipRoute,
  supportRoute,
  securityRoute,
  vaultRoute,
  walletSendRoute,
  walletHistoryRoute,
]);

// Each surface mounts the SPA under its own base path, so the shell owns it.
export const createAppRouter = (basepath: string) => createRouter({ routeTree, basepath });

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}

export const createAppQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        // Single retry covers transient mobile-network blips without
        // hammering the bot when the user is truly offline. Session-
        // terminated / auth-stale / KYC-required failures are
        // deterministic — every retry returns the same envelope until
        // the user resolves the underlying state — so skip the retry
        // and let the route's error boundary or the root-level
        // redirect handler take over.
        retry: (failureCount, error): boolean => {
          if (
            error instanceof AuthStaleError ||
            error instanceof SessionTerminatedError ||
            error instanceof KycRequiredError
          )
            return false;
          return failureCount < 1;
        },
        staleTime: 30_000,
      },
    },
  });
